import {
    Button,
    ExpandableSection,
    Link,
    SpaceBetween,
    Spinner,
    TextContent,
} from '@cloudscape-design/components';
import '../styles/dynamic-layout.scss';
import { ContentObjectsApiType } from 'src/types/useApi';
import { useApi } from 'src/hooks/useApi';
import { useEffect } from 'react';
import { DynamicLayoutContentObjects } from 'src/types/contentObject';
import { FilterItem } from './HierarchyFilters';
import { Item } from './ExpandableContainer';
import { useLocation, useNavigate } from 'react-router';
import { useTranslate } from 'src/i18n/useTranslate';
import { useArtifactDetails } from './providers/ArticleDetailsProvider';
import { DefaultFolderIcon } from './DefaultFolderIcon';

interface DynamicLayoutProps {
    title?: string;
    dynamicDrilldownOptions?: FilterItem;
    dynamicLayoutIsArtifact: boolean;
    artifactId?: string;
    dynamicArtifactImage?: string;
    filters: FilterItem[];
    setExploreModalOpen?: (isOpen: boolean) => void;
    modalVersion?: boolean;
    handleDynamicItemSelection: (props: {
        documentId?: number;
        versionId?: number;
        path?: string;
        title?: string;
    }) => void;
}

const DynamicLayout = ({
    title,
    dynamicLayoutIsArtifact = false,
    artifactId = '',
    dynamicDrilldownOptions,
    dynamicArtifactImage = '',
    filters,
    modalVersion,
    setExploreModalOpen,
    handleDynamicItemSelection,
}: DynamicLayoutProps) => {
    const navigate = useNavigate();

    const location = useLocation();
    const { pathname, hash } = location;

    const { setBreadcrumbsFromDynamicLayout } = useArtifactDetails();
    const t = useTranslate();

    const {
        makeRequest: getArtifactSectionData,
        data: artifactSectionData,
        loading: artifactSectionDataLoading,
    }: ContentObjectsApiType = useApi({
        url: `learning-artifacts/v2/${artifactId}/content-objects`,
        method: 'GET',
    });

    const navigateToDetails = (documentId: string) => {
        // set breadcrumbs
        if (setBreadcrumbsFromDynamicLayout) {
            const breadcrumbFilters = [...filters];

            if (dynamicLayoutIsArtifact) {
                breadcrumbFilters.pop();
            }

            setBreadcrumbsFromDynamicLayout(breadcrumbFilters);
        }

        // close explore modal
        if (modalVersion && setExploreModalOpen) {
            setExploreModalOpen(false);
        }

        const link = getLink(documentId);
        navigate(link);
    };

    const getLink = (documentId: string): string => {
        if (`/${documentId}` !== pathname) {
            if (
                artifactSectionData &&
                artifactSectionData.length &&
                hash !== `#/${artifactSectionData[0].lcmsXmlGUID}`
            ) {
                return `/${documentId}#/${artifactSectionData[0].lcmsXmlGUID}`;
            } else {
                return `/${documentId}`;
            }
        }

        return '#';
    };
    const sectionClick = (section: Item) => {
        if (section.isDynamic) {
            handleDynamicItemSelection({
                documentId: section.documentId,
                versionId: section.versionId,
                path: section.path,
                title: section.title,
            });
        } else {
            navigateToDetails(`${section.documentId}`);
        }
    };

    const imageClick = () => {
        if (dynamicLayoutIsArtifact) {
            navigateToDetails(artifactId);
        }
    };

    const closeModal = () => {
        // close explore modal
        if (modalVersion && setExploreModalOpen) {
            setExploreModalOpen(false);
        }
    };

    useEffect(() => {
        if (dynamicLayoutIsArtifact && artifactId) {
            getArtifactSectionData({
                url: `learning-artifacts/v2/${artifactId}/content-objects`,
            });
        }
    }, [dynamicLayoutIsArtifact, artifactId]);

    return (
        <ExpandableSection
            variant="container"
            headerText={title}
            data-testid="expandable-dynamic-section"
            expanded={true}
        >
            <div className="dynamic-layout-content-container">
                <div className="image-container">
                    <Button
                        variant="inline-link"
                        onClick={imageClick}
                        data-testid="dynamic-layout-image-button"
                    >
                        {dynamicArtifactImage || artifactId ? (
                            <img
                                src={
                                    dynamicArtifactImage ||
                                    `https://core-ar.bravais.com/api/v3/documents/${artifactId}/picture`
                                }
                                style={{ width: '100%', height: '200px' }}
                            />
                        ) : (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    padding: '20px',
                                    minHeight: '200px',
                                    alignItems: 'center',
                                }}
                            >
                                <DefaultFolderIcon size={200} />
                            </div>
                        )}
                    </Button>
                </div>
                <div className="info-container">
                    <SpaceBetween size="m">
                        <div className="header-container">
                            <TextContent>
                                {dynamicLayoutIsArtifact ? (
                                    <h3 className="dynamic-layout-artifact-link">
                                        <Link
                                            data-dynamic-layout-header-button
                                            href={getLink(artifactId)}
                                        >
                                            {title}
                                        </Link>{' '}
                                        {t(
                                            'dynamic_layout_artifact_header',
                                            'contains the following topics:'
                                        )}
                                    </h3>
                                ) : (
                                    <h3>
                                        {t(
                                            'dynamic_layout_parent_nav_header',
                                            'Use the links below to access training and documentation for: '
                                        )}{' '}
                                        {title}
                                    </h3>
                                )}
                            </TextContent>
                        </div>
                        <div className="links-container">
                            <TextContent>
                                <ul>
                                    {dynamicLayoutIsArtifact ? (
                                        !artifactSectionDataLoading ? (
                                            artifactSectionData?.map(
                                                (
                                                    section: DynamicLayoutContentObjects
                                                ) => {
                                                    return (
                                                        <li
                                                            key={
                                                                section.name +
                                                                ' ' +
                                                                section.lcmsXmlGUID
                                                            }
                                                            className="dynamic-layout-artifact-link"
                                                            data-testid="dynamic-layout-section"
                                                        >
                                                            <Link
                                                                onFollow={() => {
                                                                    closeModal();
                                                                }}
                                                                href={`${artifactId}#/${section.lcmsXmlGUID}`}
                                                            >
                                                                {section.name}
                                                            </Link>
                                                        </li>
                                                    );
                                                }
                                            )
                                        ) : (
                                            <Spinner size="normal" />
                                        )
                                    ) : (
                                        dynamicDrilldownOptions?.items?.map(
                                            (section) => {
                                                return (
                                                    <li
                                                        key={
                                                            section.path ||
                                                            section.documentId ||
                                                            section.title
                                                        }
                                                    >
                                                        <Button
                                                            variant="inline-link"
                                                            data-dynamic-layout-navigation-link
                                                            data-testid="dynamic-layout-link"
                                                            onClick={() =>
                                                                sectionClick(
                                                                    section
                                                                )
                                                            }
                                                        >
                                                            {section.title}
                                                        </Button>
                                                    </li>
                                                );
                                            }
                                        )
                                    )}
                                </ul>
                            </TextContent>
                        </div>
                    </SpaceBetween>
                </div>
            </div>
        </ExpandableSection>
    );
};

export default DynamicLayout;
